/**
 * Die Bedienelemente einer Zeitraum-Zeile — genau einmal beschriftet.
 *
 * <p>Der Dialog hat zwei Kompositionen: eine Tabelle ab genug Breite, darunter gestapelte
 * Karten. Beide greifen auf diese Bausteine zu, damit sie nicht auseinanderdriften — genau der
 * Fehler, den Block B in Liste und Kacheln und Block C in Diagramm und Tabelle gefunden hat.</p>
 *
 * <p>Jedes Feld trägt seinen eigenen Namen für die Sprachausgabe. Ein Spaltenkopf ist keiner:
 * Browser verknüpfen `th` nicht mit einem Eingabefeld darunter.</p>
 */
import { Input, Select } from '@/shared/components';
import { snapDateRange } from './helpers';
import type { AvailabilityMode, MultiRangeRow as Row } from './multiRangeRowModel';

export interface RangeControlProps {
  row: Row;
  index: number;
  onChange: (patch: Partial<Row>) => void;
  disabled: boolean;
  compact?: boolean;
}

/** „Zeitraum 2" statt einer Zeilennummer — der Nutzer zählt Zeiträume, nicht Tabellenzeilen. */
export function rangeScope(index: number) {
  return `Zeitraum ${index + 1}`;
}

const compactClass = 'py-1.5! text-xs';

export function RangeFromInput({ row, index, onChange, disabled, compact }: RangeControlProps) {
  return (
    <Input
      type="date"
      aria-label={`${rangeScope(index)}: Beginn`}
      value={row.from}
      onChange={(e) => onChange(snapDateRange(e.target.value, row.to, 'from'))}
      disabled={disabled}
      className={compact ? compactClass : undefined}
    />
  );
}

export function RangeToInput({ row, index, onChange, disabled, compact }: RangeControlProps) {
  return (
    <Input
      type="date"
      aria-label={`${rangeScope(index)}: Ende`}
      value={row.to}
      onChange={(e) => onChange(snapDateRange(row.from, e.target.value, 'to'))}
      disabled={disabled}
      className={compact ? compactClass : undefined}
    />
  );
}

export function RangeRateInput({ row, index, onChange, disabled, compact }: RangeControlProps) {
  return (
    <Input
      type="number"
      step="0.01"
      aria-label={`${rangeScope(index)}: Preis je Nacht`}
      value={row.rate}
      onChange={(e) => onChange({ rate: e.target.value })}
      placeholder="unverändert"
      disabled={disabled}
      className={compact ? compactClass : undefined}
    />
  );
}

export function RangeAvailabilitySelect({
  row,
  index,
  onChange,
  disabled,
  compact,
}: RangeControlProps) {
  return (
    <Select
      aria-label={`${rangeScope(index)}: Verfügbarkeit`}
      value={row.availability}
      onChange={(e) => onChange({ availability: e.target.value as AvailabilityMode })}
      disabled={disabled}
      className={compact ? compactClass : undefined}
    >
      <option value="">unverändert</option>
      <option value="available">Verfügbar</option>
      <option value="blocked">Blockiert</option>
    </Select>
  );
}

export function RangeMinStayInput({ row, index, onChange, disabled, compact }: RangeControlProps) {
  return (
    <Input
      type="number"
      min={1}
      max={365}
      step={1}
      aria-label={`${rangeScope(index)}: Mindestaufenthalt in Nächten`}
      value={row.minStay}
      onChange={(e) => onChange({ minStay: e.target.value })}
      placeholder="—"
      disabled={disabled}
      className={compact ? compactClass : undefined}
    />
  );
}

export function RangeMaxStayInput({ row, index, onChange, disabled, compact }: RangeControlProps) {
  return (
    <Input
      type="number"
      min={0}
      max={365}
      step={1}
      aria-label={`${rangeScope(index)}: Höchstaufenthalt in Nächten`}
      value={row.maxStay}
      onChange={(e) => onChange({ maxStay: e.target.value })}
      placeholder="—"
      disabled={disabled}
      className={compact ? compactClass : undefined}
    />
  );
}
